<svelte:options runes={false} />
<script>
  // Parameters tab (U12/KTD13): renders ALL configured parameters; the EDITABLE subset (benchmark, blast-radius
  // caps, protective-stops toggle, risk factor, sizing logic) are inline form controls that save through the
  // UpdateParams async-audit path (the same Pending→Applied/Failed lifecycle as capital adjustments). LOCKED
  // params (strategy type, symbols, frequency, mode) render read-only with a "requires redeploy" affordance.
  // Client validation mirrors the server bounds; the server re-validates (caps vs the hard ceiling, etc.).
  //
  // The live loop caches its context at run-start (KTD13), so on a RUNNING strategy an applied edit takes effect
  // on the next (re)start — we say so honestly. The persisted value + this view reflect the edit immediately.
  import api from '../../../lib/api.js';
  import { describeTarget } from '../../../lib/runContext.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { recordPendingAdjustment, selectedAdjustments } from '../../../lib/autotradeStore.js';
  import { strategyByKey } from '../../../lib/strategies.js';
  import { fromRequestSymbol, toRequestSymbol } from '../../../lib/symbolFormat.js';
  import { requestToPolicy, validatePolicy, policyPatch, diffPolicy, DEFAULT_POLICY } from '../../../lib/twapPolicy.js';
  import SymbolUniverse from '../../shared/SymbolUniverse.svelte';
  import TwapPolicyFields from '../../shared/twap/TwapPolicyFields.svelte';

  export let strategy = null;
  export let status = null;
  export let result = null;

  const FREQ_LABELS = { 60: '1m', 300: '5m', 900: '15m', 1800: '30m', 3600: '1h', 7200: '2h', 21600: '6h', 86400: '1d' };
  const SIZING_LABELS = { 0: 'Equal Split', 1: 'Inverse Volatility', 2: 'ATR-based' };

  // The configured params come from the performance result (camelCase) and the snapshot (caps/stops live there).
  $: params = result?.parameters ?? null;
  $: strategyName = params ? (strategyByKey(params.strategy)?.name ?? params.strategy) : (strategy?.strategy ?? '—');
  $: freqLabel = params ? (FREQ_LABELS[params.frequencySeconds] ?? `${params.frequencySeconds}s`)
    : (strategy ? (FREQ_LABELS[strategy.frequencySeconds] ?? `${strategy.frequencySeconds}s`) : '—');
  $: symbolsCsv = params?.symbols ?? strategy?.symbols ?? '';
  // The deployment's symbols in display form — the TWAP allow-list chips pick from these.
  $: symbolList = String(symbolsCsv).split(',').map((s) => fromRequestSymbol(s.trim())).filter(Boolean);

  // The snapshot's EFFECTIVE TWAP policy (TASK-20260823 AC 11/12; `LiveRunSnapshot.twap`, canonical BTC_USD
  // symbols) as the form's policy object in display form. Pre-feature snapshots carry none → off.
  function policyFromSnapshot(snap) {
    const p = requestToPolicy(snap?.twap ?? null);
    return { ...p, symbols: (p.symbols ?? []).map((s) => fromRequestSymbol(s)) };
  }

  // ---- Editable subset (KTD13). Seed from the current persisted values; a save sends ONLY the changed fields.
  let edit = null;
  let editKey = '';
  $: seedEdit(strategy, params);
  function seedEdit(snap, p) {
    if (!snap) { edit = null; return; }
    const key = snap.strategyId;
    if (key === editKey && edit) return; // don't clobber in-progress edits on a refetch of the same strategy
    editKey = key;
    edit = {
      riskFactor: p?.riskFactor ?? snap.riskFactor ?? 1,
      sizingLogic: p?.sizingLogic ?? snap.sizingLogic ?? 0,
      enableStopOrders: snap.enableStopOrders ?? true,
      benchmarkSymbol: p?.benchmarkSymbol ?? '',
      maxNotionalPerOrder: snap.maxNotionalPerOrder ?? '',
      maxOrdersPerWindow: snap.maxOrdersPerWindow ?? '',
      twap: policyFromSnapshot(snap),
    };
  }

  // The baseline to diff against (so we only PATCH what changed).
  $: baseline = {
    riskFactor: params?.riskFactor ?? strategy?.riskFactor ?? 1,
    sizingLogic: params?.sizingLogic ?? strategy?.sizingLogic ?? 0,
    enableStopOrders: strategy?.enableStopOrders ?? true,
    benchmarkSymbol: params?.benchmarkSymbol ?? '',
    maxNotionalPerOrder: strategy?.maxNotionalPerOrder ?? '',
    maxOrdersPerWindow: strategy?.maxOrdersPerWindow ?? '',
    twap: strategy ? policyFromSnapshot(strategy) : { ...DEFAULT_POLICY, symbols: [] },
  };

  $: twapDirty = !!edit && Object.keys(diffPolicy(baseline.twap, edit.twap)).length > 0;
  $: twapValidation = edit ? validatePolicy(edit.twap, { symbols: symbolList }) : { ok: true, errors: {}, warnings: {} };

  $: dirty = edit && baseline && (
    Number(edit.riskFactor) !== Number(baseline.riskFactor) ||
    Number(edit.sizingLogic) !== Number(baseline.sizingLogic) ||
    !!edit.enableStopOrders !== !!baseline.enableStopOrders ||
    (edit.benchmarkSymbol || '') !== (baseline.benchmarkSymbol || '') ||
    String(edit.maxNotionalPerOrder) !== String(baseline.maxNotionalPerOrder) ||
    String(edit.maxOrdersPerWindow) !== String(baseline.maxOrdersPerWindow) ||
    twapDirty
  );

  // Client-side validity (mirrors the server bounds; the server is authoritative).
  $: invalid = edit && (
    !(Number(edit.riskFactor) > 0) ||
    (edit.maxNotionalPerOrder !== '' && !(Number(edit.maxNotionalPerOrder) > 0)) ||
    (edit.maxOrdersPerWindow !== '' && !(Number(edit.maxOrdersPerWindow) > 0)) ||
    !twapValidation.ok
  );

  let saving = false;
  let saveError = null;

  function changedPatch() {
    const patch = {};
    if (Number(edit.riskFactor) !== Number(baseline.riskFactor)) patch.riskFactor = Number(edit.riskFactor);
    if (Number(edit.sizingLogic) !== Number(baseline.sizingLogic)) patch.sizingLogic = Number(edit.sizingLogic);
    if (!!edit.enableStopOrders !== !!baseline.enableStopOrders) patch.enableStopOrders = !!edit.enableStopOrders;
    if ((edit.benchmarkSymbol || '') !== (baseline.benchmarkSymbol || '')) patch.benchmarkSymbol = edit.benchmarkSymbol || '';
    if (String(edit.maxNotionalPerOrder) !== String(baseline.maxNotionalPerOrder))
      patch.maxNotionalPerOrder = edit.maxNotionalPerOrder === '' ? null : Number(edit.maxNotionalPerOrder);
    if (String(edit.maxOrdersPerWindow) !== String(baseline.maxOrdersPerWindow))
      patch.maxOrdersPerWindow = edit.maxOrdersPerWindow === '' ? null : Number(edit.maxOrdersPerWindow);
    // contract: services/AutoTrader/OrgAdjustmentModels.cs ParamsUpdateRequest.Twap — absent = unchanged,
    // `{ enabled: false }` = off, else the WHOLE policy (the server replaces the stored one). The allow-list
    // goes in request form (BTC-USD); the server canonicalizes and checks it against the strategy's symbols.
    const twap = policyPatch(baseline.twap, edit.twap); // diffed in the form's display form
    if (twap !== undefined) {
      patch.twap = typeof twap.symbols === 'string'
        ? { ...twap, symbols: twap.symbols.split(',').map((s) => toRequestSymbol(s.trim())).join(',') }
        : twap;
    }
    return patch;
  }

  async function save() {
    if (!dirty || invalid || saving) return;
    saving = true;
    saveError = null;
    try {
      const patch = changedPatch();
      const out = await api.updateStrategyParams(strategy.strategyId, patch, $selectedOrgId);
      if (out?.adjustmentId) {
        recordPendingAdjustment(out.adjustmentId, {
          requestId: strategy.strategyId, orgId: $selectedOrgId, kind: 'UpdateParams',
        });
      }
      // The persisted value is patched server-side synchronously; the next host refetch refreshes the snapshot.
      editKey = ''; // allow the next refetch to re-seed from the new baseline
    } catch (err) {
      saveError = mapError(err);
    } finally {
      saving = false;
    }
  }

  function revert() {
    editKey = '';
    seedEdit(strategy, params);
    saveError = null;
  }

  function mapError(err) {
    const msg = String(err?.message ?? err);
    const idx = msg.indexOf('details: ');
    return idx >= 0 ? msg.slice(idx + 'details: '.length) : msg;
  }

  // Pending UpdateParams adjustments for this strategy (the edit-save lifecycle strip).
  $: paramAdjustments = $selectedAdjustments.filter((a) => a.kind === 'UpdateParams');
</script>

<div class="at-params-tab">
  <!-- Locked params (read-only, requires redeploy). -->
  <section class="at-params-section">
    <h4 class="at-params-head">Configuration <span class="at-locked-note">(locked — requires redeploy)</span></h4>
    <dl class="at-params-grid">
      <div><dt>Strategy</dt><dd>{strategyName}</dd></div>
      <div class="k2-dl-span">
        <dt>Symbols</dt>
        <dd><SymbolUniverse symbols={symbolsCsv} variant="block" label="Symbols" emptyText="—" /></dd>
      </div>
      <div><dt>Frequency</dt><dd>{freqLabel}</dd></div>
      <!-- The run target (ADR-0040): "Paper on Coinbase Advanced" / "Live on Coinbase Sandbox" — read from the
           persisted snapshot, never inferred from the view; 'Unknown target' when it cannot be classified. -->
      <div><dt>Target</dt><dd>{describeTarget(strategy)}</dd></div>
      <div><dt>Starting cash (roll-up)</dt><dd>{params?.startingCash != null ? Number(params.startingCash).toLocaleString('en-US') : (strategy?.cash != null ? Number(strategy.cash).toLocaleString('en-US') : '—')}</dd></div>
    </dl>
  </section>

  <!-- Editable subset (KTD13). -->
  <section class="at-params-section">
    <h4 class="at-params-head">Risk &amp; execution <span class="at-editable-note">(editable while running)</span></h4>
    {#if edit}
      <div class="at-params-form">
        <label class="at-field">
          <span>Risk factor</span>
          <input type="number" step="0.05" min="0" class="form-input" bind:value={edit.riskFactor}
            aria-invalid={!(Number(edit.riskFactor) > 0)} />
        </label>
        <label class="at-field">
          <span>Position sizing</span>
          <select class="form-input" bind:value={edit.sizingLogic}>
            {#each Object.entries(SIZING_LABELS) as [v, l]}
              <option value={Number(v)}>{l}</option>
            {/each}
          </select>
        </label>
        <label class="at-field at-field-checkbox">
          <input type="checkbox" bind:checked={edit.enableStopOrders} />
          <span>Protective stops</span>
        </label>
        <label class="at-field">
          <span>Benchmark symbol</span>
          <input type="text" class="form-input" bind:value={edit.benchmarkSymbol} placeholder="(none)" />
        </label>
        <label class="at-field">
          <span>Max notional / order</span>
          <input type="number" step="1" min="0" class="form-input" bind:value={edit.maxNotionalPerOrder}
            placeholder="server default" aria-invalid={edit.maxNotionalPerOrder !== '' && !(Number(edit.maxNotionalPerOrder) > 0)} />
        </label>
        <label class="at-field">
          <span>Max orders / window</span>
          <input type="number" step="1" min="0" class="form-input" bind:value={edit.maxOrdersPerWindow}
            placeholder="server default" aria-invalid={edit.maxOrdersPerWindow !== '' && !(Number(edit.maxOrdersPerWindow) > 0)} />
        </label>
      </div>

      <!-- TWAP execution (TASK-20260823 AC 12): the same section as the deploy form, editable while running.
           Goes-forward: the loop reads the policy at every placement, so the NEXT order honours a saved edit;
           a parent already working keeps the schedule it was placed with. -->
      <div class="at-twap-edit">
        <TwapPolicyFields bind:policy={edit.twap} symbols={symbolList} errors={twapValidation.errors}
          warnings={twapValidation.warnings} showConcurrency={true} disabled={saving} idPrefix="at-params-twap" />
        <p class="at-twap-note">
          Applies to the next order placed after saving — in-flight parents are not changed by an edit.
          Protective stops stay native.
        </p>
      </div>

      {#if saveError}
        <div class="alert alert-danger" role="alert">{saveError}</div>
      {/if}

      <div class="at-params-actions">
        <button type="button" class="btn btn-primary" disabled={!dirty || invalid || saving} on:click={save}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" class="btn btn-ghost" disabled={!dirty || saving} on:click={revert}>Revert</button>
        {#if dirty && String(status) === 'Running'}
          <span class="at-redeploy-note">
            {twapDirty ? 'TWAP changes apply to the next order; the other edits take effect on the strategy\'s next restart.' : 'Applied edits take effect on the strategy\'s next restart.'}
          </span>
        {/if}
      </div>

      {#if paramAdjustments.length}
        <ul class="at-pending-strip" aria-label="Parameter change status">
          {#each paramAdjustments as a (a.id)}
            <li class="at-pending-item at-pending-{String(a.status).toLowerCase()}">
              <span class="at-pending-kind">Parameter update</span>
              <span class="at-pending-status">{a.status}{a.failureReason ? ` — ${a.failureReason}` : ''}</span>
            </li>
          {/each}
        </ul>
      {/if}
    {:else}
      <p class="empty-state-description">No editable parameters available.</p>
    {/if}
  </section>
</div>

<style>
  .at-params-section { margin-bottom: 1.25rem; }
  .at-params-head { margin: 0 0 0.5rem; font-size: 0.9rem; font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .at-locked-note { font-weight: 400; font-size: 0.75rem; color: var(--text-muted, #8a8b8d); }
  .at-editable-note { font-weight: 400; font-size: 0.75rem; color: var(--accent, #3c78f0); }
  .at-params-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.5rem 1.25rem; margin: 0; }
  .at-params-grid > div { display: flex; flex-direction: column; gap: 0.1rem; }
  .at-params-grid dt { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-muted, #8a8b8d); }
  .at-params-grid dd { margin: 0; font-size: 0.85rem; color: var(--text-primary, #e8e9ea); font-variant-numeric: tabular-nums; }
  .at-params-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem 1.25rem; }
  .at-field { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  .at-field-checkbox { flex-direction: row; align-items: center; gap: 0.4rem; }
  .at-params-actions { display: flex; align-items: center; gap: 0.75rem; margin-top: 0.85rem; flex-wrap: wrap; }
  .at-redeploy-note { font-size: 0.75rem; color: var(--text-muted, #8a8b8d); }
  .at-twap-edit { margin-top: 0.85rem; display: flex; flex-direction: column; gap: 0.35rem; }
  .at-twap-note { margin: 0; font-size: 0.75rem; color: var(--text-muted, #8a8b8d); }
  .at-pending-strip { list-style: none; margin: 0.75rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.3rem; }
  .at-pending-item { display: flex; justify-content: space-between; gap: 0.75rem; padding: 0.35rem 0.6rem; border-radius: 5px; font-size: 0.78rem; background: var(--surface-1, rgba(255, 255, 255, 0.03)); }
  .at-pending-pending { border-left: 3px solid var(--warning, #d8a000); }
  .at-pending-applied { border-left: 3px solid var(--success, #1aa260); }
  .at-pending-failed { border-left: 3px solid var(--danger, #e0533a); }
  .at-pending-status { color: var(--text-muted, #8a8b8d); font-variant-numeric: tabular-nums; }
</style>
